import React, { useState, useEffect } from 'react'
import styles from './AdditionalQuestionsModal.module.css'
import axios from 'axios'

const AdditionalQuestionsModal = ({ 
  isOpen,
  onClose,
  onComplete,
  topicId,
  imageURL,
  coords
 }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [loadingDots, setLoadingDots] = useState('.')

  // 테스트 종류별 질문 정의
  const questionsByTopic = {
    'htp-house': [
      {
        number: 'Q0',
        question: '그림의 제목은 무엇인가요?',
        placeholder: '예: 아빠와 나'
      },
      {
        number: 'Q1',
        question: '당신이 그린 집에 누가 살고 있나요?',
        placeholder: '예: 가족, 나, 친구들'
      },
      {
        number: 'Q2',
        question: '집 안에 들어가면 어떤 느낌이 드나요?',
        placeholder: '예: 포근하고 편안한 마음이 들어요.'
      },
      {
        number: 'Q3',
        question: '당신이 그린 집에서 가장 마음에 드는 부분은 어디인가요? 그 이유는요?',
        placeholder: '예: 지붕이 가장 마음에 들어요. 왜냐하면..'
      }
    ],
    'htp-tree': [
      {
        number: 'Q0',
        question: '그림의 제목은 무엇인가요?',
        placeholder: '예: 나의 나무'
      },
      {
        number: 'Q1',
        question: '이 나무는 어떤 곳에 자라고 있나요?',
        placeholder: '예: 산속에 자라고 있어요.'
      },
      {
        number: 'Q2',
        question: '이 나무는 얼마나 오래됐을까요?',
        placeholder: '예: 100년 정도 된 것 같아요.'
      },
      {
        number: 'Q3',
        question: '이 나무에 무엇이 달려 있거나 살고 있나요?',
        placeholder: '예: 새들이 살고 있어요.'
      }
    ],
    'htp-human': [
      {
        number: 'Q0',
        question: '그림의 제목은 무엇인가요?',
        placeholder: '예: 엄마의 모습'
      },
      {
        number: 'Q1',
        question: '이 사람은 누구인가요?',
        placeholder: '예: 우리 엄마에요.'
      },
      {
        number: 'Q2',
        question: '이 사람은 지금 무슨 생각을 하고 있나요?',
        placeholder: '예: 행복한 생각을 하는 것 같아 보여요.'
      },
      {
        number: 'Q3',
        question: '이 사람은 어떤 상황에 처해 있나요?',
        placeholder: '예: 요리를 할 준비를 하고 있어요.'
      }
    ],
    pitr: [
      {
        number: 'Q0',
        question: '그림의 제목은 무엇인가요?',
        placeholder: '예: 행복했던 날'
      },
      {
        number: 'Q1',
        question: '이 사람은 왜 비를 맞고 있나요?',
        placeholder: '예: 우산을 안 가지고 나왔어요.'
      },
      {
        number: 'Q2',
        question: '이 사람은 지금 어떤 기분일까요?',
        placeholder: '예: 비에 맞아 기분이 안 좋아요.'
      },
      {
        number: 'Q3',
        question: '비를 피할 수 있다면 어떤 방법을 쓸까요?',
        placeholder: '예: 비가 그칠 때까지 그늘에 숨어 있을 거에요.'
      }
    ]
  }

  const currentQuestions = questionsByTopic[topicId] || questionsByTopic['htp-house']
  const currentQuestion = currentQuestions[currentStep]
  const totalSteps = currentQuestions.length

  const [answers, setAnswers] = useState(Array(currentQuestions.length).fill(''))

  const handleAnswerChange = (e) => {
    const newAnswers = [...answers]
    newAnswers[currentStep] = e.target.value
    setAnswers(newAnswers)
  }

  // topicId가 변경되면 answers 초기화
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0)
      setAnswers(Array(currentQuestions.length).fill(''))
    }
  }, [topicId, isOpen])

  useEffect(() => {
    if (submitLoading) {
      const interval = setInterval(() => {
        setLoadingDots((prev) => {
          if (prev === '.') return '..'
          if (prev === '..') return '...'
          return '.'
        })
      }, 500) // 0.5초마다 변경

      return () => clearInterval(interval)
    }
  }, [submitLoading])

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1)
    } else {
      const title = answers[0] || '제목 없음'

      const followUpQuestions = answers.slice(1).map((answer) => answer || '')

      onComplete({
        title,
        followUpQuestions,
        answers,
      })
    }
  }

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    } else {
      onClose()
    }
  }

  const handleCancel = () => {
    setCurrentStep(0)
    setAnswers(Array(currentQuestions.length).fill(''))
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h2>추가 질문</h2>
          <p>정확성을 위해 몇 가지만 물어볼게요!</p>
          <span className={styles.stepIndicator}>({currentStep + 1}/{totalSteps})</span>
        </div>

        <div className={styles.questionSection}>
          <h3 className={styles.questionNumber}>{currentQuestion.number}.</h3>
          <p className={styles.questionText}>{currentQuestion.question}</p>
          
          <textarea
            className={styles.answerInput}
            value={answers[currentStep]}
            onChange={handleAnswerChange}
            placeholder={currentQuestion.placeholder}
            rows={4}
          />
        </div>

        <div className={styles.actions}>
          {currentStep === 0 && (
            <button 
              type="button"
              className={styles.prevBtn}
              onClick={handleCancel}
              disabled={submitLoading}
            >
              취소
            </button>
          )}
          {currentStep > 0 && (
            <button className={styles.prevBtn} onClick={handlePrev} disabled={submitLoading}>
              뒤로
            </button>
          )}
          <button 
            className={styles.nextBtn}
            onClick={handleNext}
            disabled={!answers[currentStep]?.trim()}
          >
            {submitLoading ? (
              <>
                제출중
                <span className={styles.loadingDots}>{loadingDots}</span>
              </>
            ) : (
              currentStep === totalSteps - 1 ? '완료' : '다음'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AdditionalQuestionsModal